import React from "react";
import styled from "styled-components";

const Wrapper = styled.div`
  padding: 0rem 2rem 4rem;
  max-width: 900px;
  margin: auto;
  background: #fff;
  color: #1f2937;
  line-height: 1.6;
  margin-top: 90px;

  h1,
  h2,
  h3 {
    color: #111827;
    margin-top: 2rem;
  }

  ul,
  ol {
    margin-left: 1.5rem;
  }

  a {
    color: #3b82f6;
    text-decoration: none;
  }

  p {
    margin-top: 1rem;
  }
`;

const TermsAndConditions: React.FC = () => (
  <Wrapper>
    <h1>Terms and Conditions</h1>
    <p>
      <strong>Last updated:</strong> October 28, 2026
    </p>

    <h2>1. Acceptance of Terms</h2>
    <p>
      These Terms and Conditions ("Terms") govern your access to and use of
      VendorOrbis (the "Service"), including our web and mobile applications.
      By accessing or using the Service, you agree to these Terms and our
      Privacy Policy. If you do not agree, do not use the Service.
    </p>

    <h2>2. The Service</h2>
    <p>
      VendorOrbis is a business tool for event-based sellers. The Service helps
      you track events, inventory, sales, and finances, and provides AI-powered
      insights and recommendations. The Service is for record-keeping and
      analytics only.
    </p>

    <h2>3. No Payment Processing</h2>
    <p>
      VendorOrbis does not process, transmit, or settle payments. We do not act
      as a payment processor, money transmitter, or financial institution. Any
      payment method labels or QR payment codes you add are stored for display
      and record-keeping only. If you enable Square card payments, Square
      (Block, Inc.) processes those card transactions. You are responsible for
      complying with applicable payment rules and laws, and your use of Square
      is governed by Square’s terms and privacy policy. (This is about
      payments you take from your own customers. Payments for your
      VendorOrbis plan are covered in Section 8.)
    </p>

    <h2>4. Eligibility and Accounts</h2>
    <ul>
      <li>
        <strong>Eligibility:</strong> You must be at least 18 years old and able
        to form a binding contract.
      </li>
      <li>
        <strong>Account info:</strong> You agree to provide accurate, current,
        and complete information and to keep it updated.
      </li>
      <li>
        <strong>Security:</strong> You are responsible for safeguarding your
        account credentials and all activity under your account.
      </li>
    </ul>

    <h2>5. Acceptable Use</h2>
    <ul>
      <li>Do not use the Service for illegal, harmful, or abusive activity.</li>
      <li>Do not interfere with or disrupt the Service or its security.</li>
      <li>Do not reverse engineer, decompile, or attempt to extract source code.</li>
      <li>Do not upload malicious code or attempt unauthorized access.</li>
    </ul>

    <h2>6. Your Content and Business Data</h2>
    <p>
      You retain ownership of the content and data you submit ("Your Content").
      You grant VendorOrbis a non-exclusive, worldwide license to host, store,
      back up, process, and display Your Content solely to operate and improve
      the Service. You are responsible for having the rights and permissions
      needed to provide Your Content.
    </p>

    <h2>7. AI Features</h2>
    <p>
      The Service may provide AI-generated insights, summaries,
      recommendations, and action items. These outputs are informational and
      may be incomplete or inaccurate. You are responsible for decisions and
      actions you take based on these outputs. Do not rely on AI outputs as
      legal, financial, or professional advice.
    </p>

    <h2>8. Subscriptions</h2>
    <h3>Plans and prices</h3>
    <p>
      VendorOrbis offers a Free plan with limits on events and inventory, and
      paid plans billed monthly or yearly. Current prices are shown on our
      pricing page and at checkout. Prices on our website may differ from
      prices in the iPhone or Android app. Prices do not include taxes unless
      stated, and we may collect sales tax where required.
    </p>
    <h3>Where you subscribe</h3>
    <p>
      You can subscribe in the app, through the Apple App Store or Google
      Play, or on our website. Website subscriptions are handled by RevenueCat
      and paid by card through Stripe; we do not store full payment card
      details. App Store and Google Play subscriptions are also subject to
      Apple's or Google's terms.
    </p>
    <h3>Free trials</h3>
    <p>
      Some plans include a free trial for eligible new subscribers. Unless you
      cancel before the trial ends, it becomes a paid subscription and you are
      charged the plan price.
    </p>
    <h3>Renewal and payment</h3>
    <p>
      Subscriptions renew automatically at the end of each billing period
      until you cancel. By subscribing, you authorize us and our payment
      providers to charge your payment method at each renewal. If a payment
      fails, we may retry it; if it can't be collected, your account moves to
      the Free plan. If we change the price of your plan, we will tell you in
      advance, and the new price applies from your next renewal.
    </p>
    <h3>Cancelling</h3>
    <p>
      You can cancel at any time. Cancel a website subscription from the
      Billing page or the link in your receipt email; cancel an App Store or
      Google Play subscription in that store (deleting the app does not
      cancel it). Cancelling stops future charges, and you keep your plan
      until the end of the period you have paid for. After that, your account
      moves to the Free plan and its limits apply; your data stays in your
      account.
    </p>
    <h3>Refunds</h3>
    <p>
      Payments are non-refundable, and we do not give refunds or credits for
      partial billing periods, except where required by law. Refunds for App
      Store and Google Play purchases are handled by Apple or Google under
      their policies. If you believe you were charged in error, contact us at{" "}
      <a href="mailto:support@vendororbis.com">support@vendororbis.com</a>.
    </p>

    <h2>9. Third-Party Services</h2>
    <p>
      The Service relies on third-party providers for hosting, analytics,
      notifications, and AI processing. Your use of those services may be
      subject to their terms and policies.
    </p>
    <h3>Square card payments</h3>
    <p>
      If you enable Square card payments, Square (Block, Inc.) processes card
      transactions. Your use of Square is governed by Square’s terms and
      privacy policy:
    </p>
    <ul>
      <li>
        <a href="https://squareup.com/legal" target="_blank" rel="noreferrer">
          Square Terms of Service
        </a>
      </li>
      <li>
        <a
          href="https://squareup.com/us/en/legal/general/privacy"
          target="_blank"
          rel="noreferrer"
        >
          Square Privacy Policy
        </a>
      </li>
    </ul>

    <h3>Permissions for Square payments</h3>
    <p>
      To connect Square readers and enable Tap to Pay, the mobile app may
      request device permissions. These are required only to make Square
      payments work:
    </p>
    <ul>
      <li>
        <strong>Location</strong> – required by Square to discover and connect
        nearby readers.
      </li>
      <li>
        <strong>Bluetooth / Nearby Devices</strong> – required to connect and
        communicate with Square readers.
      </li>
      <li>
        <strong>Microphone (Android)</strong> – required for some reader
        hardware types.
      </li>
      <li>
        <strong>Phone State (Android)</strong> – required by Square to identify
        the device for reader pairing.
      </li>
    </ul>

    <h2>10. Suspension and Termination</h2>
    <p>
      We may suspend or terminate your account if you violate these Terms or if
      required by law. You may request deletion of your account and data by
      contacting us at <strong>support@vendororbis.com</strong>. Some data may
      be retained as required by law or for legitimate business purposes.
    </p>

    <h2>11. Disclaimers</h2>
    <p>
      THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
      ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF
      MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
    </p>

    <h2>12. Limitation of Liability</h2>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, VENDORORBIS WILL NOT BE LIABLE FOR
      INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, OR
      FOR LOSS OF PROFITS, REVENUE, OR DATA, ARISING OUT OF OR RELATED TO YOUR
      USE OF THE SERVICE.
    </p>

    <h2>13. Governing Law</h2>
    <p>
      These Terms are governed by the laws of the jurisdiction where
      VendorOrbis is headquartered, without regard to conflict of law
      principles. If you live outside that jurisdiction, mandatory consumer
      protections in your country may still apply.
    </p>

    <h2>14. Changes to These Terms</h2>
    <p>
      We may update these Terms from time to time. We will post updates in the
      Service and may notify you by email or in-app notice. Continued use after
      changes means you accept the updated Terms.
    </p>

    <p>&copy; {new Date().getFullYear()} VendorOrbis. All rights reserved.</p>
  </Wrapper>
);

export default TermsAndConditions;
